import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Button, DropdownMenu, QuantityInput, SanityImage } from 'components';
import { LocalContext } from 'context';
import { flushSync } from 'react-dom';
import { WhiteCrossmark as Crossmark } from 'assets/svgs/';
import { customDropdownMenuStyles } from 'styles';

function CartItem({ item }) {
  const { quote, setQuote, counterRef, setCartCounter } = useContext(LocalContext);
  const { dimensions, otherDimensions } = item;
  const [quantity, setQuantity] = useState(item.quantity);

  const [selectedProduct, setSelectedProduct] = useState({
    label: dimensions.label,
    value: {
      productId: item.productId,
      dimensions: dimensions.value.dimensions
    },
    order: dimensions.order
  });

  const dropdownOptions = [dimensions, ...otherDimensions].sort((a, b) => a.order - b.order);

  const handleEditDimensions = (_selectedProduct) => {
    setSelectedProduct(_selectedProduct);
    setQuote((currentQuote) =>
      currentQuote.map((_item) =>
        _item.otherDimensions
          .map(({ value }) => value.productId)
          .includes(_selectedProduct.value.productId)
          ? {
              ..._item,
              productId: _selectedProduct.value.productId,
              dimensions: _selectedProduct,
              otherDimensions: [
                ..._item.otherDimensions.filter(({ label }) => label !== _selectedProduct.label),
                _item.dimensions
              ]
            }
          : _item
      )
    );
  };

  const handleEditQuantity = async (newQuantity) => {
    setQuote((_currentQuote) =>
      _currentQuote.map((_item) => {
        if (_item.productId.toLowerCase() === item.productId.toLowerCase()) {
          _item.quantity = newQuantity;
        }
        return _item;
      })
    );

    const _quantity = quote?.reduce((acc, _item) => {
      const sum = acc + _item.quantity;
      return sum;
    }, 0);

    setCartCounter(_quantity);
  };

  const handleDeleteFromCart = async () => {
    if (document.startViewTransition) {
      counterRef.current.style.viewTransitionName = 'cart-counter';

      const incrementCounterTransition = document.startViewTransition(() =>
        flushSync(() => {
          setCartCounter((currentValue) => currentValue - item.quantity);
        })
      );

      await incrementCounterTransition.finished;

      counterRef.current.style.viewTransitionName = 'none';
    } else {
      flushSync(() => {
        setCartCounter((currentValue) => currentValue - item.quantity);
      });
    }
    setQuote((currentQuote) =>
      currentQuote.filter(({ productId }) => productId !== item.productId)
    );
  };

  return (
    <Container>
      <StyledSanityImage
        image={item.image}
        imageWidth={121}
        imageHeight={154}
        alt={item.title}
        title={item.title}
      />
      <h4>{item.title}</h4>
      <p>Size:</p>
      {otherDimensions.length ? (
        <DropdownMenu
          value={[selectedProduct, ...otherDimensions].filter(
            ({ label }) => label === selectedProduct.label
          )}
          onChange={handleEditDimensions}
          placeholder={selectedProduct.label}
          styles={customDropdownMenuStyles({ margin: 0 })}
          options={dropdownOptions}
          isSearchable={false}
        />
      ) : (
        <OnlyOneDimensionAvailable>
          <b>{selectedProduct.label}</b>
        </OnlyOneDimensionAvailable>
      )}
      <p>Qty:</p>
      <QuantityInput quantity={quantity} setQuantity={setQuantity} callback={handleEditQuantity} />
      <DeleteFromCart onClick={handleDeleteFromCart}>
        <Crossmark />
      </DeleteFromCart>
    </Container>
  );
}

const Container = styled(motion.li).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
})`
  align-items: start;
  background-color: transparent;
  column-gap: 12px;
  display: grid;
  grid-template-columns: 121px 1fr;
  grid-template-rows: repeat(5, auto);
  position: relative;
  width: 100%;

  /* Size dropdown arrow */
  svg {
    width: 13px;
  }

  > :not(div) {
    grid-column: 2/3;
    padding-right: 38px;
  }

  > p {
    margin: 0.45rem 0 0.2rem;
  }

  > div:not(:first-of-type) {
    margin: unset;
  }
`;

const OnlyOneDimensionAvailable = styled.p`
  bottom: 6px;
  margin: 0 !important;
  position: relative;
`;

const StyledSanityImage = styled(SanityImage)`
  border-radius: 6px;
  grid-column: 1/2;
  grid-row: 1/-1;
  height: 154px;
  width: 121px;
`;

const DeleteFromCart = styled(Button)`
  height: 26px !important;
  min-width: unset !important;
  padding: unset !important;
  position: absolute !important;
  right: 0 !important;
  top: 0 !important;
  width: 26px !important;

  svg {
    width: 18px;
  }
`;

export default CartItem;
